import { z } from "zod";
import { Id, IsoDate, Locale, Page, PageQuery } from "./common.ts";
import { CaptionRequest, ImageGenRequest, MediaAsset, Post } from "./domain.ts";
import type { RouteDef } from "./routes.ts";

/**
 * Every AI generation is a job (owner rule, after a bare "image failed"): image, caption/text, post score, rule from a
 * prompt, voice (TTS/STT) and calendar "create post for this day" drafts run as jobs-center operations (kind `ai.*`).
 * The UI shows progress in place and in the jobs tray; the user can leave the page: on completion a notification + toast
 * ("Your image is ready" → View), on failure a notification with the typed reason (AI_JOB_ERROR_CODES) and Retry (the
 * jobs-center retry re-runs the same input). Results stay attached to the job (`operations.result` = AiGenerationResult),
 * so the Jobs center previews them and "Use in post" opens the composer with the media. AI Studio history = these jobs.
 */
export const AI_JOB_KINDS = ["ai.image", "ai.image_edit", "ai.caption", "ai.score", "ai.rule", "ai.tts", "ai.stt", "ai.occasion"] as const;
export const AiJobKind = z.enum(AI_JOB_KINDS);
export type AiJobKindT = (typeof AI_JOB_KINDS)[number];

/** Typed failure codes of AI jobs (stored in operations.error_code; the UI localizes each one + offers Retry). */
export const AI_JOB_ERROR_CODES = ["ai_unavailable", "budget_exceeded", "limit_reached", "feature_not_in_plan", "storage_error", "content_policy", "timeout", "validation", "unknown"] as const;
export const AiJobErrorCode = z.enum(AI_JOB_ERROR_CODES);

export const StartAiJobRequest = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ai.image"), input: ImageGenRequest }),
  /** AI edit of an image (LiteLLM /v1/images/edits, model malfak-image): the result is a NEW media asset linked to the source. */
  z.object({ kind: z.literal("ai.image_edit"), input: z.lazy(() => ImageEditRequest.extend({ mediaId: Id })) }),
  z.object({ kind: z.literal("ai.caption"), input: CaptionRequest }),
  z.object({ kind: z.literal("ai.occasion"), input: CaptionRequest.extend({ occasion: z.object({ eventId: Id }) }) }),
  z.object({ kind: z.literal("ai.score"), input: z.object({ caption: z.string().max(2200), type: Post.shape.type }) }),
  z.object({ kind: z.literal("ai.rule"), input: z.object({ prompt: z.string().min(5).max(1000) }) }),
  z.object({ kind: z.literal("ai.tts"), input: z.object({ text: z.string().min(1).max(2000), voice: z.string().default("ar-AE-FatimaNeural") }) }),
  z.object({ kind: z.literal("ai.stt"), input: z.object({ mediaId: Id, language: z.string().default("ar-AE") }) }),
]);
export type StartAiJobRequestT = z.infer<typeof StartAiJobRequest>;

/**
 * Body of `POST /ai/images/:mediaId/edit`: what to change, optionally only inside a mask (a PNG uploaded to the media
 * library first; transparent pixels = the region to change). Size defaults to the source's closest supported size.
 */
export const ImageEditRequest = z.object({
  prompt: z.string().trim().min(3).max(2000),
  size: z.enum(["1024x1024", "1024x1536", "1536x1024"]).optional(),
  maskMediaId: Id.optional(),
});

/** What a finished AI job produced (operations.result). Media ids are media-library rows (the Jobs center previews them). */
export const AiGenerationResult = z.object({
  mediaIds: z.array(Id).default([]),
  media: z.array(MediaAsset).default([]),
  /** Captions (ai.caption / ai.occasion). */
  options: z.array(z.object({ caption: z.string(), hashtags: z.array(z.string()) })).optional(),
  /** ai.score */
  score: z.object({ score: z.number(), checks: z.array(z.object({ key: z.string(), ok: z.boolean(), tip: z.string() })) }).optional(),
  /** ai.rule: the drafted RuleInput (validated by the API). */
  rule: z.unknown().optional(),
  /** ai.stt transcript. */
  text: z.string().optional(),
  costUsd: z.number().nullable().optional(),
});
export type AiGenerationResultT = z.infer<typeof AiGenerationResult>;

/** One AI Studio history row (= one ai.* job). */
export const AiGeneration = z.object({
  jobId: Id,
  kind: AiJobKind,
  status: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
  /** Short human summary of the input (prompt, brief, text…), untrusted user text. */
  prompt: z.string(),
  input: z.record(z.string(), z.unknown()),
  result: AiGenerationResult.nullable(),
  error: z.string().nullable(),
  errorCode: z.string().nullable(),
  /** Retry = the jobs-center retry (same input); a retry's new job points back via retryOf. */
  retryOf: Id.nullable(),
  createdBy: Id.nullable(),
  createdAt: IsoDate,
  finishedAt: IsoDate.nullable(),
});
export type AiGenerationT = z.infer<typeof AiGeneration>;
export const AiGenerationsQuery = PageQuery.extend({
  kind: AiJobKind.optional(),
  status: z.enum(["active", "succeeded", "failed", "all"]).default("all"),
  mine: z.enum(["true", "false"]).optional(),
});

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const OrgP = z.object({ orgId: z.string().uuid() });
const GenP = OrgP.extend({ jobId: z.string().uuid() });
/** Job shape returned by startAiJob = the jobs-center Job (kept loose here to avoid an import cycle: see domain.Job). */
export const aiJobRoutes = {
  startAiJob: route({ method: "POST", path: "/v1/orgs/:orgId/ai/jobs", params: OrgP, body: StartAiJobRequest, response: AiGeneration, status: 202, summary: "Start an AI generation as a job (progress in the jobs tray; notification + toast when done; Retry from the jobs center)" }),
  aiGenerations: route({ method: "GET", path: "/v1/orgs/:orgId/ai/generations", params: OrgP, query: AiGenerationsQuery, response: Page(AiGeneration), summary: "AI Studio history: past generations (prompt, result thumbnails, status), newest first" }),
  aiGeneration: route({ method: "GET", path: "/v1/orgs/:orgId/ai/generations/:jobId", params: GenP, response: AiGeneration, summary: "One generation with its result" }),
  editImage: route({ method: "POST", path: "/v1/orgs/:orgId/ai/images/:mediaId/edit", params: OrgP.extend({ mediaId: z.string().uuid() }), body: ImageEditRequest, response: AiGeneration, status: 202, summary: "AI edit of an image as a job (ai.image_edit): the result is a new media asset, a version of the source (never overwrites it)" }),
  deleteAiGeneration: route({ method: "DELETE", path: "/v1/orgs/:orgId/ai/generations/:jobId", params: GenP, response: z.object({ ok: z.literal(true) }), summary: "Remove a generation from the history (its media stays in the library unless unused)" }),
} as const;

export const AiLocale = Locale;
